/**
 * Europe/London date logic for What's On.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE EXISTS
 *
 * The Phase 1 audit (EVENTS_AUDIT.md §2.3) found "tonight", "this weekend"
 * and friends being encoded as *category strings* on the event row, so a
 * karaoke night filed under `category='Karaoke'` and a Saturday gig filed
 * under `category='Quiz Night'` were both, in effect, claiming to be a date.
 *
 * Nothing in this module stores a date bucket. Every preset is a pure
 * function of `new Date()` evaluated in **Europe/London**, resolved to a
 * concrete inclusive `YYYY-MM-DD` range, and compared against the real
 * `event_date` / occurrence date returned by the API.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY Europe/London AND NOT THE BROWSER ZONE
 *
 * The web app is a Vite SPA: all date maths runs on the visitor's machine.
 * A user in Spain (UTC+2) opening the page at 00:30 local is still at 23:30
 * on the *previous* British day, and "tonight" must mean the British day —
 * the pub is in Kilmarnock, not Malaga. So the civil ("wall clock") date is
 * read out of `Intl.DateTimeFormat` pinned to Europe/London, and all
 * arithmetic is then done on that civil date.
 *
 * Arithmetic uses `Date.UTC`, never local time. UTC has no DST, so
 * `addDays` can never skip or repeat a day across the March/October
 * transitions — which a naive `d.setDate(d.getDate() + 1)` in a DST zone
 * can, at exactly the hour the clocks change.
 */

export const LONDON_TZ = "Europe/London";

/** A civil (wall-clock) date. No time, no offset — just what a calendar says. */
export interface CivilDate {
  y: number;
  /** 1–12. Not the JS 0-based month. */
  m: number;
  d: number;
}

/** An inclusive `YYYY-MM-DD` range. `from === to` is a single day. */
export interface DateRange {
  from: string;
  to: string;
}

const londonPartsFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: LONDON_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** Today's date *in Ayrshire*, whatever zone the browser is in. */
export function londonToday(now: Date = new Date()): CivilDate {
  const parts = londonPartsFormatter.formatToParts(now);
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { y: read("year"), m: read("month"), d: read("day") };
}

const pad2 = (n: number) => String(n).padStart(2, "0");

export function toISO(c: CivilDate): string {
  return `${c.y}-${pad2(c.m)}-${pad2(c.d)}`;
}

/**
 * Parse `YYYY-MM-DD`. Anything else returns null rather than an
 * Invalid Date that silently poisons every comparison downstream —
 * the audit (§3.2) found 11 of 19 `event_time` values unparseable, so
 * defensive parsing here is not theoretical.
 */
export function fromISO(s: string | null | undefined): CivilDate | null {
  if (!s || !/^\d{4}-\d{2}-\d{2}/.test(s)) return null;
  const [y, m, d] = s.slice(0, 10).split("-").map(Number) as [number, number, number];
  if (!y || !m || !d) return null;
  return { y, m, d };
}

/** DST-proof day arithmetic — UTC has no clock changes to fall into. */
export function addDays(c: CivilDate, n: number): CivilDate {
  const t = new Date(Date.UTC(c.y, c.m - 1, c.d + n));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}

/** 0 = Sunday … 6 = Saturday. */
export function weekday(c: CivilDate): number {
  return new Date(Date.UTC(c.y, c.m - 1, c.d)).getUTCDay();
}

/** Negative if a < b, 0 if equal, positive if a > b. ISO strings sort correctly. */
export function compareISO(a: string, b: string): number {
  return a.localeCompare(b);
}

/**
 * The Friday→Sunday block the given day belongs to (or the next one).
 *
 * Fri/Sat/Sun are treated as already inside a weekend, so on a Sunday
 * "this weekend" still means today — not next week's. Mon–Thu look forward
 * to the coming Friday.
 *
 * `clampToToday` drops the part of the block that has already happened, so
 * asking on a Saturday never offers you Friday.
 */
function weekendBlock(today: CivilDate, clampToToday = true): DateRange {
  const dow = weekday(today);
  // How far back the block's Friday is. -1 means the block has not started.
  const daysSinceFriday = dow === 5 ? 0 : dow === 6 ? 1 : dow === 0 ? 2 : -1;

  const friday =
    daysSinceFriday >= 0
      ? addDays(today, -daysSinceFriday)
      : addDays(today, (5 - dow + 7) % 7);
  const sunday = addDays(friday, 2);

  const start = clampToToday && daysSinceFriday > 0 ? today : friday;
  return { from: toISO(start), to: toISO(sunday) };
}

/** The Friday→Sunday block *after* the current one. Never clamped — it is entirely future. */
function nextWeekendBlock(today: CivilDate): DateRange {
  const dow = weekday(today);
  const daysSinceFriday = dow === 5 ? 0 : dow === 6 ? 1 : dow === 0 ? 2 : -1;
  const thisFriday =
    daysSinceFriday >= 0
      ? addDays(today, -daysSinceFriday)
      : addDays(today, (5 - dow + 7) % 7);
  const friday = addDays(thisFriday, 7);
  return { from: toISO(friday), to: toISO(addDays(friday, 2)) };
}

/* ── Presets ──────────────────────────────────────────────────────────── */

export type DatePresetId =
  | "any"
  | "tonight"
  | "tomorrow"
  | "this-weekend"
  | "next-weekend"
  | "pick";

export interface DatePreset {
  id: DatePresetId;
  label: string;
  /** URL segment, where one exists. `/events/tonight`, `/events/this-weekend`. */
  slug?: string;
}

export const DATE_PRESETS: readonly DatePreset[] = [
  { id: "any", label: "Any date" },
  { id: "tonight", label: "Tonight", slug: "tonight" },
  { id: "tomorrow", label: "Tomorrow", slug: "tomorrow" },
  { id: "this-weekend", label: "This weekend", slug: "this-weekend" },
  { id: "next-weekend", label: "Next weekend", slug: "next-weekend" },
  { id: "pick", label: "Pick a date" },
] as const;

/** URL segment → preset id. Used by the `/events/:segment` route resolver. */
export const DATE_PRESET_BY_SLUG: Record<string, DatePresetId> = {
  tonight: "tonight",
  today: "tonight",
  tomorrow: "tomorrow",
  "this-weekend": "this-weekend",
  weekend: "this-weekend",
  "next-weekend": "next-weekend",
};

/**
 * Resolve a preset to a concrete inclusive range, or `null` for "no date
 * constraint". `pickedDate` is only read for the `pick` preset.
 *
 * `now` is injectable so this is unit-testable without freezing the clock.
 */
export function rangeForPreset(
  preset: DatePresetId,
  pickedDate?: string | null,
  now: Date = new Date(),
): DateRange | null {
  const today = londonToday(now);

  switch (preset) {
    case "tonight": {
      const iso = toISO(today);
      return { from: iso, to: iso };
    }
    case "tomorrow": {
      const iso = toISO(addDays(today, 1));
      return { from: iso, to: iso };
    }
    case "this-weekend":
      return weekendBlock(today);
    case "next-weekend":
      return nextWeekendBlock(today);
    case "pick": {
      const picked = fromISO(pickedDate);
      if (!picked) return null;
      const iso = toISO(picked);
      return { from: iso, to: iso };
    }
    case "any":
    default:
      return null;
  }
}

/** True when `dateStr` (YYYY-MM-DD) falls inside the inclusive range. */
export function inRange(dateStr: string | null | undefined, range: DateRange | null): boolean {
  if (!range) return true;
  if (!dateStr) return false;
  const d = dateStr.slice(0, 10);
  return d >= range.from && d <= range.to;
}

/* ── Presentation helpers ─────────────────────────────────────────────── */

const LONG_DATE = new Intl.DateTimeFormat("en-GB", {
  timeZone: "UTC",
  weekday: "long",
  day: "numeric",
  month: "long",
});
const SHORT_DATE = new Intl.DateTimeFormat("en-GB", {
  timeZone: "UTC",
  weekday: "short",
  day: "numeric",
  month: "short",
});

/**
 * Render an ISO date as a UTC-anchored Date so the formatter cannot shift it
 * across a midnight boundary. The value is a *civil* date; it has no instant,
 * so formatting it in any zone other than the one we built it in is a bug.
 */
function asUTCDate(dateStr: string): Date | null {
  const c = fromISO(dateStr);
  return c ? new Date(Date.UTC(c.y, c.m - 1, c.d)) : null;
}

/** "Tonight" / "Tomorrow" / "Saturday 29 August". Relative to Europe/London. */
export function groupLabel(dateStr: string, now: Date = new Date()): string {
  const today = toISO(londonToday(now));
  const tomorrow = toISO(addDays(londonToday(now), 1));
  const d = dateStr.slice(0, 10);
  if (d === today) return "Tonight";
  if (d === tomorrow) return "Tomorrow";
  const utc = asUTCDate(d);
  return utc ? LONG_DATE.format(utc) : d;
}

/** "Sat 29 Aug". For cards and inline metadata. */
export function shortDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const utc = asUTCDate(dateStr.slice(0, 10));
  return utc ? SHORT_DATE.format(utc) : dateStr;
}

/** `{ month: "Aug", day: 29, weekday: "Sat" }` for the calendar-tile badge. */
export function dateBadgeParts(dateStr: string | null | undefined): {
  month: string;
  day: string;
  weekday: string;
} | null {
  const utc = asUTCDate((dateStr ?? "").slice(0, 10));
  if (!utc) return null;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    weekday: "short",
  }).formatToParts(utc);
  const read = (t: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === t)?.value ?? "";
  return { month: read("month"), day: read("day"), weekday: read("weekday") };
}

/**
 * Best-effort time rendering.
 *
 * `event_time` is free text on 11 of 19 audited rows ("BBQ 3pm DISCO 8pm",
 * "13:00 till 21:00", "7-9.30", "20:00 " with a trailing space). We normalise
 * a clean `HH:MM` to "8pm" / "8:30pm" and otherwise return the author's own
 * words verbatim — losing "DOORS 1pm SHOW 2pm" would be worse than showing it.
 */
export function formatTime(time: string | null | undefined): string {
  if (!time) return "";
  const trimmed = time.trim();
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(trimmed);
  if (!m) return trimmed;
  const hour = Number(m[1]);
  const minute = m[2]!;
  if (hour > 23) return trimmed;
  const suffix = hour >= 12 ? "pm" : "am";
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return minute === "00" ? `${h12}${suffix}` : `${h12}:${minute}${suffix}`;
}

/** Weekday names indexed the JS way (0 = Sunday), for recurring activities. */
export const DAY_NAMES_FULL = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
] as const;
export const DAY_NAMES_SHORT = [
  "Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat",
] as const;
